import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';

import { getTrialAttributes } from '@features/budget-attributes/services/budget-attributes.service';

import { ButtonComponent } from '@shared/components/button/button.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { TablePanelComponent } from '@shared/components/table-panel/table-panel.components';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  EntitySettingTypes,
  EntityType,
  EventType,
  GqlService,
  WorkflowStep,
} from '@shared/services/gql.service';
import { injectFlags, LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService, injectPermissions } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { GlobalStore } from '@shared/store/global/global.store';
import { MainQuery } from '@shared/store/main/main.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { ExportType } from '@shared/utils/utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TablePanelComponent,
    ButtonComponent,
    FormsModule,
    TooltipDirective,
    TableSkeletonComponent,
    CheckboxComponent,
  ],
  styles: [
    `
      :host ::ng-deep .bg-gray-100:not([type='checkbox']:checked) {
        background-color: #e5e6ec;
      }
    `,
  ],
  templateUrl: 'je-configuration.component.html',
})
export class JeConfigurationComponent {
  authService = inject(AuthService);
  eventService = inject(EventService);
  globalStore = inject(GlobalStore);
  gqlService = inject(GqlService);
  launchDarklyService = inject(LaunchDarklyService);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  router = inject(Router);
  workflowQuery = inject(WorkflowQuery);

  flags = injectFlags({
    isIMAVisible: 'tab_in_month_adjustments',
    isJEVisible: 'tab_journal_entries',
    isMQCVisible: 'month_quarter_close_tab',
  });
  permissions = injectPermissions({
    canUpdateJEConfiguration: 'PERMISSION_UPDATE_JOURNAL_ENTRY_CONFIGURATION',
  });

  editMode = signal(false);
  // it should be linked signal :/
  hierarchy = signal<string[]>([]);

  attributes = computed(() => getTrialAttributes(this.globalStore.attributes()));
  editBtnDisabled = computed(() => {
    const errArr: string[] = [];

    if (this.isWorkflowLocked()) {
      errArr.push(
        'Vendor Expenses are locked for Period Close. Navigate to Period Close > Checklist and unlock to make change.'
      );
    }

    if (!this.permissions().canUpdateJEConfiguration) {
      errArr.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    return errArr;
  });
  initialHierarchy = computed(() => {
    const hierarchyAttributes = [
      this.globalStore.primaryAttribute.attribute_name(),
      this.globalStore.secondaryAttribute.attribute_name(),
    ].flatMap((attribute) => (attribute ? [attribute] : []));

    return hierarchyAttributes.length ? hierarchyAttributes : ['Cost Category'];
  });
  hasChanges = computed(
    () => this.initialHierarchy().sort().toString() !== this.hierarchy().sort().toString()
  );
  hierarchyOptions = computed<
    {
      disabled: boolean;
      label: string;
      value: string;
    }[]
  >(() => [
    ...this.attributes().map((attr) => ({
      disabled: !attr.attribute_values_populated,
      label: attr.attribute_name,
      value: attr.attribute_name,
    })),
  ]);

  attributesLoading = this.globalStore.getLoading('TrialAttributes');

  loading = computed(() => this.attributesLoading());
  saveBtnDisabled = computed(() => {
    if (!this.hasChanges()) return 'No changes to save.';

    if (!this.hierarchy().length) return 'Select at least 1 attribute grouping to save.';

    return '';
  });

  _syncHierarchy = effect(() => {
    if (this.globalStore.hierarchyAttributesLoaded()) {
      const hierarchyAttributes = [
        this.globalStore.primaryAttribute.attribute_name(),
        this.globalStore.secondaryAttribute.attribute_name(),
      ].flatMap((attribute) => (attribute ? [attribute] : []));

      this.hierarchy.set(hierarchyAttributes.length ? hierarchyAttributes : ['Cost Category']);
    }
  });
  hasEditHierarchyFF = this.launchDarklyService.$select(
    (flags) => flags.edit_journal_entry_configuration
  );
  isWorkflowLocked = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_ADJUSTMENTS
  );

  constructor() {
    this.globalStore.refreshRequest('TrialAttributes');
  }

  onCancel() {
    this.editMode.set(false);
    this.hierarchy.set(this.initialHierarchy());
  }

  onCostCategorySet(event: boolean) {
    if (event) this.hierarchy.set(['Cost Category']);
    else this.hierarchy.set([]);
  }

  async onExport() {
    const trialShortName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const trialId = this.mainQuery.getSelectedTrial()?.id || '';
    const fileTimestamp = dayjs().format('YYYY.MM.DD-HHmmss');
    const currentTz = dayjs.tz.guess();

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: trialId,
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_entity_id: trialId || '',
          export_type: ExportType.JOURNAL_ENTRY_CONFIGURATION,
          filename: `${trialShortName}_auxilius_journal_entry_configuration_${fileTimestamp}`,
          json_properties: {
            fileTimestamp: `${fileTimestamp} ${currentTz}`,
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  }

  onHierarchySet(event: boolean, value: string) {
    const hierarchy = this.hierarchy();

    if (event) {
      this.hierarchy.set([
        ...hierarchy.filter((hierarchyValue) => hierarchyValue !== 'Cost Category'),
        value,
      ]);

      return;
    }

    this.hierarchy.set([...hierarchy.filter((hierarchyValue) => hierarchyValue !== value)]);
  }

  // eslint-disable-next-line max-lines-per-function
  onSave() {
    const trial_id = this.mainQuery.getValue().trialKey;

    this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        keywordToExecuteAction: 'Update Journal Entry Configuration',
        message:
          'Are you sure you want to update your Update Journal Entry Configuration? Updating is not recommended. This will update the way your Expense and Invoices data is grouped. This may impact closed, current, and future Close Summary and Journal Entries. However, monthly audit packages generated at each month close will still be available in the Document Library.',
        showNotesSection: {
          documentsConfig: {
            path: `trials/${trial_id}/je-configuration/`,
            required: false,
          },
          noteConfig: {
            required: true,
          },
        },
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Update Journal Entry Configuration?',
        headerIcon: {
          color: 'var(--aux-red-dark)',
          name: 'AlertTriangle',
        },
        primaryButton: {
          action: async (instance: ConfirmationActionModalComponent | null) => {
            if (instance) {
              const notes = instance.notes();
              const { fileQuery, fileService } = instance.fileManager()!;
              const bucket_keys: string[] = fileQuery.getAll().map((file) => file.bucket_key);

              await fileService.uploadFiles({
                metadata: { admin: '1' },
              });

              const hierarchyAttributes = this.hierarchy()
                .filter((attr) => attr !== 'Cost Category')
                .sort();

              const { data, errors, success } = await firstValueFrom(
                this.gqlService.updateEntitySettings$({
                  entity_ids: [trial_id],
                  entity_type: EntityType.TRIAL,
                  setting_type: EntitySettingTypes.CLOSE_HIERARCHY,
                  settings: JSON.stringify({
                    ATTRIBUTES: {
                      PRIMARY: hierarchyAttributes.at(0),
                      SECONDARY: hierarchyAttributes.at(1),
                    },
                    BUCKET_KEYS: bucket_keys,
                    NOTES: notes,
                  }),
                })
              );

              if (success && data) {
                this.globalStore.refreshRequest('TrialAttributes');
                this.editMode.set(false);
                instance.ref.close();
              } else {
                this.overlayService.error('Something went wrong. Please try again.');
                console.error('Error updating entity settings', errors);
              }
            }
          },
          label: 'Save',
          variant: 'primary',
        },
        secondaryButton: {
          label: 'Cancel',
          variant: 'secondary',
        },
      },
    });
  }

  viewJournalEntries() {
    const { isIMAVisible, isJEVisible, isMQCVisible } = this.flags();
    let route = `/${ROUTING_PATH.BUDGET.INDEX}/${ROUTING_PATH.BUDGET.INDEX}`;

    if (isIMAVisible) {
      route = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.EXPENSE_DETAIL}`;
    }

    if (isMQCVisible) {
      route = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.CLOSE_SUMMARY}`;
    }

    if (isJEVisible) {
      route = `/${ROUTING_PATH.CLOSING.INDEX}/${ROUTING_PATH.CLOSING.JOURNAL_ENTRIES}`;
    }

    void this.router.navigateByUrl(route);
  }
}
